/**
 * Pure logic for the Goal Questionnaire (SFCC-11290) — extracted from the
 * components so the quiz branching + benefits matching are unit-testable without
 * React. No hooks, no DOM.
 */
import { ModelMap } from "~/hec/builder/model-map";

import type {
  GoalData,
  GoalEntry,
  QuestionAnswer,
  QuestionData,
} from "./types";

/** True when an answer's result branches to a goal (terminal) vs a next question. */
export const isAnswerGoal = (answer: QuestionAnswer): boolean =>
  answer.result.model === ModelMap.learningCenterGoal;

/**
 * Resolve an answer's goal result (SFCC-11740): look the goal up in the
 * pre-loaded list by entry id (`result.id` is the goal entry hash), else fall
 * back to an inlined `result.value.data`. Stamp the resolved goal with its
 * Builder `entryId` (the entry hash) so the benefits-table highlight can match
 * on the stable hash rather than the author-set `data.id` slug (which can be
 * absent or non-unique). Returns undefined if neither resolves.
 *
 * The list lookup is preferred over the inline value because the entry hash is
 * only known via the list (a bare/inline `result` carries the same `data` but
 * pairs with `result.id` for the hash); both paths carry `entryId`.
 */
export const resolveGoal = (
  answer: QuestionAnswer,
  goals: GoalEntry[],
): GoalData | undefined => {
  const fromList = goals.find(g => g.id === answer.result.id);
  if (fromList) return { ...fromList.data, entryId: fromList.id };

  const inlined = answer.result.value?.data as GoalData | undefined;
  return inlined ? { ...inlined, entryId: answer.result.id } : undefined;
};

/**
 * Whether a goal-list entry is the currently-recommended goal (SFCC-11740).
 * Matches on the Builder entry hash first (the stable join key carried as
 * `entryId`), falling back to the author-set `data.id` slug for goals resolved
 * before the hash was stamped. Returns false when there's no recommendation.
 */
export const isRecommendedGoal = (
  currentGoal: GoalData | null | undefined,
  goalEntry: GoalEntry,
): boolean => {
  if (!currentGoal) return false;
  if (currentGoal.entryId) return currentGoal.entryId === goalEntry.id;
  return currentGoal.id === goalEntry.data.id;
};

/**
 * The questions array after selecting a "next question" answer at `currentIndex`:
 * drop any later (post-Back) questions, then append the next unless it's already
 * present (dedupe by question text). Pure — caller advances the index.
 */
export const appendNextQuestion = (
  questions: QuestionData[],
  currentIndex: number,
  next: QuestionData,
): QuestionData[] => {
  const base = questions.slice(0, currentIndex + 1);
  return base.some(q => q.question === next.question) ? base : [...base, next];
};

/** Trim the questions array to the current slide (used when a goal is reached). */
export const truncateQuestions = (
  questions: QuestionData[],
  currentIndex: number,
): QuestionData[] => questions.slice(0, currentIndex + 1);

/** The benefit display strings a goal carries (enriched refs → `value.data.benefit`). */
export const goalBenefitNames = (goal: GoalData | null | undefined): string[] =>
  (goal?.benefits ?? [])
    .map(({ benefit }) => benefit?.value?.data?.benefit)
    .filter((b): b is string => Boolean(b));

/** Whether a goal carries a given benefit (by display string). */
export const goalHasBenefit = (goal: GoalData, benefitValue: string): boolean =>
  goalBenefitNames(goal).includes(benefitValue);
